/* eslint-disable no-nested-ternary */
import Image from "@Components/Image";
import { shallowEqual, useSelector } from "react-redux";
import { AppState } from "@Store/index";
import Subsection from "../Subsection";
import POPULAR_COUNTRIES, {
  CITY_BASE_URL,
  COUNTRY_ZH_MAP,
  PopularCountry,
} from "./data/popularCountries";
import styles from "./popularCitites.module.scss";
import { useEffect, useRef, useState } from "react";
import formatClassNames from "@Utils/clientUtils/stringUtility/formatClassNames";
import { PAGE } from "constants/configConstants";
import gtagEventEmitter from "helpers/gtagEventEmitter";
import Chevron from "@Icons/Chevron";
import CustomLink from "@Components/CustomLink";
import useTranslationHook from "hooks/useTranslationHook";
import wrapperHOC from "@Utils/wrapperHOC";
import customHistoryPush from "@Utils/customHistoryPush";
import useHistory from "hooks/useHistory";
import useWhitelabel from "hooks/useWhitelabel";
import { getRegions } from "services";
import Shimmer from "@Components/Shimmer/Shimmer";
import { getWhiteLabelSearchRedirectionURL } from "@Pages/WhitelabelHome/Desktop/helpers/WhiteLabelRedirectionHelper";
import config from "@Config/index";
import useIsLanguage from "hooks/useIsLanguage";
import getUserCountry from "@Pages/Search/components/SearchList/components/TrustPilotBanner/helper";
import countryCallingCodes from "constants/countryCallingCodes";
import { SCREEN_SIZE, useResponsiveness } from "hooks/useResponsiveness";
import useCustomWindowOpen from "@Components/CustomWindowOpen";
import getImagePath from "@Utils/getImagePath";
import { getSearchSuggestions } from "@Components/SearchModalMobile/data/helper";
import { useFeatureIsOn } from "@growthbook/growthbook-react";
import { GROWTH_BOOK_FEATURE_FLAG_NAMES } from "constants/index";

const getFlagUrl = (flagImage: string | null): string => {
  if (!flagImage) return "";
  return `${config.IMAGE_STATIC_URL}/images/${flagImage}`;
};

// mweb shows a shortened one-line subtitle; desktop reads the original, longer copy from the
// same key plus a "desktop" suffix
const SUBTITLE_KEY = "home.collectionrailsgroup.popularcities.popularcitiessubtitle";

const PopularCities = ({
  isDesktop = false,
  isCustomPadding = false,
  fromBaidu = false,
  // Defaults to the grey band this section has always had. The home page overrides it because the
  // rails around it now carry the grey; every other page that renders this keeps the old look.
  greyBg,
  isRepeatUser = false,
}: {
  isDesktop?: boolean;
  isCustomPadding?: boolean;
  fromBaidu?: boolean;
  greyBg?: boolean;
  isRepeatUser?: boolean;
}) => {
  const [activeCountry, setActiveCountry] = useState<string>("United Kingdom");
  const customWindowOpen = useCustomWindowOpen();
  const domesticCountryRef = useRef<string>("United Kingdom");
  const { isAbove } = useResponsiveness();
  const isAboveMD = isAbove(SCREEN_SIZE.MD, false, true);
  const showForDesktop = isDesktop && isAboveMD;

  const isPersonalizedSearchEnabled =
    useFeatureIsOn(GROWTH_BOOK_FEATURE_FLAG_NAMES.PERSONALIZED_SEARCH_SUGGESTIONS) ||
    config.IS_CHINA_APP;

  // Dynamic countries/cities from API
  const [dynamicCountries, setDynamicCountries] = useState<any[]>([]);
  const [apiLoaded, setApiLoaded] = useState(false);

  const { isSaleBannerActiveOnHomePage } = useSelector(
    ({ appData }: AppState) => ({
      isSaleBannerActiveOnHomePage: appData.isSaleBannerActiveOnHomePage,
    }),
    shallowEqual,
  );
  const isAmberFest = isSaleBannerActiveOnHomePage === "amber-fest";

  const mapIsoToPopularName = (iso?: string): PopularCountry | null => {
    if (!iso) return null;
    const longName = countryCallingCodes[iso as keyof typeof countryCallingCodes]?.name;
    if (!longName) return null;
    const matched =
      POPULAR_COUNTRIES.find((c) => c.name === longName || longName.includes(c.name)) || null;
    return (matched?.name as PopularCountry) || null;
  };

  // Build a lookup map of static city images: { "canonical_name" → "image_url" }
  const staticCityImageMap = useRef<Record<string, string>>({});
  if (Object.keys(staticCityImageMap.current).length === 0) {
    POPULAR_COUNTRIES.forEach((country) => {
      country.popular_cities.forEach((city) => {
        if (city.canonical_name && city.image) {
          staticCityImageMap.current[city.canonical_name] = city.image;
        }
      });
    });
  }

  // Resolve city image: static image if city exists in static data, else CDN pattern
  const getCityImage = (cityName: string, canonicalName: string, countryName: string): string => {
    if (canonicalName && staticCityImageMap.current[canonicalName]) {
      return staticCityImageMap.current[canonicalName];
    }
    const countrySlug = countryName.replace(/\s+/g, "_");
    const citySlug = cityName
      .normalize("NFD")
      .replace(/[\u0300-\u036f]/g, "")
      .replace(/\s+/g, "_");
    return `${CITY_BASE_URL}Popular-Cities/${countrySlug}/${citySlug}.png`;
  };

  // Fetch search suggestions from API and map to countries/cities
  const fetchSuggestionsData = async () => {
    const suggestionsRes: any = await getSearchSuggestions({ isPersonalizedSearchEnabled });
    const responseData = suggestionsRes?.data?.data;

    if (responseData?.personalized && responseData?.data?.length > 0) {
      // Filter out the ALL tab and map API data to component format
      const apiCountries = responseData.data
        .filter((country: any) => country.country_full_name !== "ALL")
        .map((country: any) => {
          // Look up the Chinese name from static data for translated country alias
          const staticMatch = POPULAR_COUNTRIES.find(
            (c) => c.name === country.country_full_name || c.shortName === country.country_alias,
          );
          const nameZh =
            staticMatch?.name_zh ||
            COUNTRY_ZH_MAP[country.country_full_name] ||
            COUNTRY_ZH_MAP[country.country_alias] ||
            country.country_alias ||
            country.country_full_name;
          return {
            id: country.id,
            name: country.country_full_name,
            name_zh: nameZh,
            shortName: country.country_alias,
            icon: getFlagUrl(country.country_flag_image),
            popular_cities: (country.top_cities?.data || []).map((city: any) => ({
              id: city.id,
              name: city.name,
              name_zh: city.name_zh || city.name,
              canonical_name: city.canonical_name,
              image: getCityImage(city.name, city.canonical_name, country.country_full_name),
            })),
          };
        });

      if (apiCountries.length > 0) {
        setDynamicCountries(apiCountries);
        setApiLoaded(true);
        // Set active tab to domestic country if it exists in API response, else first
        const domesticInApi = apiCountries.find((c: any) => c.name === domesticCountryRef.current);
        setActiveCountry(domesticInApi ? domesticInApi.name : apiCountries[0].name);
      }
    }
  };

  useEffect(() => {
    (async () => {
      // Detect user country for domestic tab ordering
      const lsCode = typeof window !== "undefined" ? localStorage.getItem("country") : undefined;
      const res = lsCode ? { country: lsCode } : await getUserCountry();
      const countryName = mapIsoToPopularName(res?.country) || "United Kingdom";
      domesticCountryRef.current = countryName;
      setActiveCountry(countryName);

      // Fetch suggestions (may use non-personalized if flag hasn't resolved yet)
      await fetchSuggestionsData();
    })();
  }, []);

  // Re-fetch when GrowthBook resolves the personalized flag (it starts as false on mount)
  const hasRefetchedForPersonalized = useRef(false);

  const [showLeft, setShowLeft] = useState(false);
  const [showRight, setShowRight] = useState(true);
  const { t } = useTranslationHook();
  const { isWhitelabel, whitelabelConfig, regionCanonicalNames, isDemoSubdomain, partner_uuid } =
    useWhitelabel();
  const [whitelabelActiveRegions, setWhitelabelActiveRegions] = useState<any>([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (
      isPersonalizedSearchEnabled &&
      !apiLoaded &&
      !hasRefetchedForPersonalized.current &&
      !isWhitelabel
    ) {
      hasRefetchedForPersonalized.current = true;
      fetchSuggestionsData();
    }
  }, [isPersonalizedSearchEnabled]);

  // Order countries: move domestic country to front (for static fallback)
  const orderedCountries = (() => {
    if (apiLoaded) {
      // API response is already ordered by booking share; no reordering needed
      return dynamicCountries;
    }
    const domesticCountryObj = POPULAR_COUNTRIES.find((c) => c.name === domesticCountryRef.current);
    return domesticCountryObj
      ? [
          domesticCountryObj,
          ...POPULAR_COUNTRIES.filter((c) => c.name !== domesticCountryRef.current),
        ]
      : POPULAR_COUNTRIES;
  })();

  const popularCitiesConst = isWhitelabel
    ? whitelabelConfig?.multi_region_text?.title || "Cities where our campuses are located"
    : t("home.collectionrailsgroup.popularcities.popularcitiestitle");
  const bookSubDescription = isWhitelabel
    ? whitelabelConfig?.multi_region_text?.sub_title || t(`${SUBTITLE_KEY}desktop`)
    : t(`${SUBTITLE_KEY}${isDesktop ? "desktop" : ""}`);
  const history = useHistory();
  const userLanguage = useIsLanguage();

  const popularCities = (() => {
    const source = apiLoaded ? dynamicCountries : POPULAR_COUNTRIES;
    return source.find((country: any) => country.name === activeCountry)?.popular_cities || [];
  })();

  const handlePillClick = (country: any) => {
    gtagEventEmitter({
      action: "clicked_country_across_globe",
      event_category: PAGE.HOME,
    });
    setActiveCountry(country.name);
    scrollDiv.current.scrollLeft = 0;
  };

  const handleCityClick = (name: string, canonical_name: any, e: any, redirectionLink?: string) => {
    e.preventDefault();
    e.stopPropagation();

    if (isWhitelabel && redirectionLink) {
      customWindowOpen({ url: redirectionLink, target: "_blank", isExternal: true });
      return;
    }
    const searchPath = isWhitelabel
      ? getWhiteLabelSearchRedirectionURL(
          whitelabelConfig,
          canonical_name,
          isDemoSubdomain,
          partner_uuid,
        )
      : `/places/search/${canonical_name}`;
    customHistoryPush(history, searchPath);
    gtagEventEmitter({
      action: "clicked_city_across_globe",
      category: PAGE.HOME,
      canonical_name,
    });
    gtagEventEmitter({
      action: "clicked_popular_city_card",
      category: PAGE.HOME,
      label: `${activeCountry} - ${name}`,
      canonical_name,
    });
  };

  const handleLeftClick = () => {
    if (scrollDiv.current)
      scrollDiv.current.scrollTo({
        left: scrollDiv.current.scrollLeft - 242,
        behavior: "smooth",
      });
    setShowRight(
      scrollDiv.current.scrollLeft + scrollDiv.current.clientWidth <
        scrollDiv.current.scrollWidth - 242,
    );
    setShowLeft(scrollDiv.current.scrollLeft > 242);
  };
  const handleRightClick = () => {
    if (scrollDiv.current)
      scrollDiv.current.scrollTo({
        left: scrollDiv.current.scrollLeft + 242,
        behavior: "smooth",
      });
    setShowLeft(scrollDiv.current.scrollLeft > 242);
    setShowRight(
      scrollDiv.current.scrollLeft + scrollDiv.current.clientWidth <
        scrollDiv.current.scrollWidth - 242,
    );
  };
  const scrollDiv: any = useRef();

  const handleOnScroll = () => {
    setShowLeft(scrollDiv.current.scrollLeft > 0);
    setShowRight(
      scrollDiv.current.scrollLeft + scrollDiv.current.clientWidth < scrollDiv.current.scrollWidth,
    );
  };

  useEffect(() => {
    setShowLeft(scrollDiv.current.scrollLeft > 0);
    setShowRight(
      scrollDiv.current.scrollLeft + scrollDiv.current.clientWidth < scrollDiv.current.scrollWidth,
    );
  }, [activeCountry]);

  const getMultiRegionsData = async (canonicalNames: string) => {
    const regionOptions = {
      url_path: "/api/v0/regions",
      params: {
        only: "id,name,canonical_name,images",
        canonical_names: canonicalNames,
      },
    };
    const { data } = await getRegions(regionOptions);
    return data.data.result;
  };

  useEffect(() => {
    if (isWhitelabel) {
      const fetchRegionsData = async () => {
        setLoading(true);
        try {
          const canonicalNames = regionCanonicalNames.map((r) => r.value).join(",");
          const data = await getMultiRegionsData(canonicalNames);
          if (partner_uuid === "bimm-a014fd89") {
            data.push({
              canonical_name: "essex-bimm-whitelabel",
              id: 12345,
              images: [
                {
                  caption: "London – Rayleigh",
                  path: `${config.IMAGE_STATIC_URL}/images/london-rayleigh.jpg`,
                  type: "cover",
                },
              ],
              name: "London – Rayleigh",
              redirectionLink: "https://performerscollege.co.uk/essex/accommodation/",
            });
          }

          data?.sort((a: { [key: string]: any }, b: { [key: string]: any }) =>
            a.name.localeCompare(b.name),
          );
          setWhitelabelActiveRegions(data);
        } catch (error) {
          console.error("Error fetching regions data:", error);
        } finally {
          setLoading(false);
        }
      };

      fetchRegionsData();
    }
  }, [regionCanonicalNames]);

  const getCountry = (country: any) => {
    if (config.IS_CHINA_APP || userLanguage === "zh") {
      return country.name_zh || country.shortName || country.name;
    }
    if (!isDesktop) {
      if (country.shortName) return country.shortName;
      if (country.name === "United Kingdom") return "UK";
      if (country.name === "United States") return "USA";
    }
    return country.name;
  };

  const TOP_LIMIT = isDesktop ? (isWhitelabel ? 10 : 8) : isWhitelabel ? 3 : 2;

  return (
    <Subsection
      title={popularCitiesConst}
      subtitle={bookSubDescription}
      subSectionPaddingReduced={showForDesktop}
      withSidePadding={isDesktop}
      greyBg={greyBg ?? !isWhitelabel}
      isDesktop={showForDesktop}
      isCustomPadding={isCustomPadding}
      isWhitelabel={isWhitelabel}
      className={formatClassNames(
        !isDesktop && styles.subsectionWithNoBottomPadding,
        isRepeatUser && isAmberFest && styles.extraMarginTop,
      )}
    >
      {!isWhitelabel && (
        <div
          className={formatClassNames(
            styles.pillsContainer,
            isDesktop && styles.pillsContainerDesktop,
          )}
        >
          {orderedCountries.map((country) => (
            <div
              className={formatClassNames(
                styles.pill,
                activeCountry === country.name && styles.activePill,
                isDesktop && styles.pillsDesktop,
              )}
              onClick={() => handlePillClick(country)}
              data-testid={country.name}
              aria-label={country.name}
              onKeyDown={(event) => {
                if (event.key === "Enter") {
                  handlePillClick(country);
                }
              }}
              role="button"
              tabIndex={0}
              key={country?.id}
            >
              <Image
                src={country.icon}
                height={isDesktop ? 16 : 40}
                width={isDesktop ? 16 : 40}
                alt=""
              />
              <span>{getCountry(country)}</span>
            </div>
          ))}
        </div>
      )}
      <div
        className={formatClassNames(
          styles.hrscContainer,
          isDesktop && styles.desktop,
          isDesktop && isWhitelabel && styles.subsectionWithNoPadding,
          !isDesktop && isWhitelabel && styles.transparentBackground,
        )}
      >
        <div
          ref={scrollDiv}
          onScroll={handleOnScroll}
          className={formatClassNames(
            isWhitelabel
              ? whitelabelActiveRegions?.length <= TOP_LIMIT
                ? styles.citiesFlex
                : styles.WhitelableCitiesGrid
              : popularCities?.length < TOP_LIMIT
              ? styles.citiesFlex
              : styles.citiesGrid,
            isDesktop && styles.citiesGridDesktop,
            isDesktop && isWhitelabel && styles.subsectionWithNoPadding,
            (!isDesktop || whitelabelActiveRegions?.length > TOP_LIMIT) &&
              isWhitelabel &&
              styles.spaceEvenly,
          )}
          {...(isWhitelabel && {
            style: {
              gridTemplateColumns: `repeat(${Math.ceil(whitelabelActiveRegions?.length / 2)}, 1fr)`,
            },
          })}
        >
          {isWhitelabel
            ? whitelabelActiveRegions?.map((city: any) => (
                <CustomLink
                  key={city.id}
                  className={formatClassNames(styles.cityTile, isDesktop && styles.cityTileDesktop)}
                  role="none"
                  to={getWhiteLabelSearchRedirectionURL(
                    whitelabelConfig,
                    city.canonical_name,
                    isDemoSubdomain,
                    partner_uuid,
                  )}
                  onClick={(e) =>
                    handleCityClick(city.name, city.canonical_name, e, city?.redirectionLink)
                  }
                  data-testid={city.name}
                >
                  <div className={styles.overlay} />

                  {loading ? (
                    <div className={styles.shimmer}>
                      <Shimmer width="200" height="200" noMargin />
                    </div>
                  ) : (
                    <Image
                      src={getImagePath(
                        city?.images?.length > 0
                          ? city.images[0].path
                          : "https://prod-assets.amberstudent.com/regions/56/65e8b9b2.jpg",
                        "",
                        { w: 600, h: 600, q: 100 },
                      )}
                      height={isDesktop ? 200 : 140}
                      width={isDesktop ? 200 : 140}
                      className={styles.icon}
                      alt={userLanguage === "zh" ? city.name_zh : city.name}
                    />
                  )}
                  <span className={(isDesktop || isWhitelabel) && styles.regionName}>
                    {userLanguage === "zh" ? city.name_zh : city.name}
                  </span>
                </CustomLink>
              ))
            : popularCities?.map((city: any) =>
                fromBaidu ? (
                  <div
                    key={city.id}
                    className={formatClassNames(
                      styles.cityTile,
                      isDesktop && styles.cityTileDesktop,
                    )}
                    role="none"
                    data-testid={city.name}
                  >
                    <div className={styles.overlay} />
                    <Image
                      src={city.image}
                      height={isDesktop ? 200 : 140}
                      width={isDesktop ? 200 : 140}
                      className={styles.icon}
                      alt={userLanguage === "zh" ? city.name_zh : city.name}
                    />
                    <span>{userLanguage === "zh" ? city.name_zh : city.name}</span>
                  </div>
                ) : (
                  <CustomLink
                    key={city.id}
                    className={formatClassNames(
                      styles.cityTile,
                      isDesktop && styles.cityTileDesktop,
                    )}
                    role="none"
                    to={`/places/search/${city.canonical_name}`}
                    onClick={(e) => handleCityClick(city.name, city.canonical_name, e)}
                    data-testid={city.name}
                    id={`${city.name}_${fromBaidu}`}
                  >
                    <div className={styles.overlay} />

                    <Image
                      src={getImagePath(city.image, "small")}
                      height={isDesktop ? 200 : 140}
                      width={isDesktop ? 200 : 140}
                      className={styles.icon}
                      alt={userLanguage === "zh" ? city.name_zh : city.name}
                    />
                    <span className={styles.textWrap}>
                      {userLanguage === "zh" ? city.name_zh : city.name}
                    </span>
                  </CustomLink>
                ),
              )}
        </div>
        {isDesktop && (
          <div>
            {showLeft && (
              <div
                data-testid="left-carousel"
                onClick={handleLeftClick}
                role="none"
                className={formatClassNames(styles.wrapper, styles.left)}
              >
                <div className={styles.icon}>
                  <Chevron width="12" height="12" />
                </div>
              </div>
            )}
            {showRight && (
              <div
                data-testid="right-carousel"
                onClick={handleRightClick}
                role="none"
                className={formatClassNames(styles.wrapper, styles.right)}
              >
                <div className={styles.icon}>
                  <Chevron width="12" height="12" />
                </div>
              </div>
            )}
          </div>
        )}
      </div>
    </Subsection>
  );
};

export default wrapperHOC(PopularCities, {
  componentName: "PopularCities-Home",
  showForChina: true,
});
